import type { DepartmentTask, WorkOrder, WorkOrderStatus } from './types';

export interface WorkOrderDashboardFilters {
  query: string;
  clientId: string;
  dateFrom: string;
  dateTo: string;
  status: WorkOrderStatus | 'all';
}

export type WorkOrderListView = 'active' | 'completed' | 'deleted';

export function filterWorkOrdersForView(
  orders: WorkOrder[],
  view: WorkOrderListView,
): WorkOrder[] {
  if (view === 'deleted') return orders.filter((order) => Boolean(order.deletedAt));
  return orders.filter((order) => !order.deletedAt && order.status === view);
}

export function filterWorkOrders(
  orders: WorkOrder[],
  filters: WorkOrderDashboardFilters,
): WorkOrder[] {
  const query = filters.query.trim().toLowerCase();
  return orders.filter((order) => {
    if (filters.clientId && order.clientId !== filters.clientId) return false;
    if (filters.dateFrom && order.orderDate < filters.dateFrom) return false;
    if (filters.dateTo && order.orderDate > filters.dateTo) return false;
    if (filters.status !== 'all' && order.status !== filters.status) return false;
    if (!query) return true;
    return [order.orderNumber, order.customerName, order.details]
      .some((value) => value.toLowerCase().includes(query));
  });
}

export function taskProgressLabel(task: DepartmentTask): string {
  if (task.status === 'completed') {
    return `${task.departmentName} · Done by ${task.finishedByName ?? task.pickedUpByName ?? 'Unknown user'}`;
  }
  if (task.status === 'started') {
    return `${task.departmentName} · Started by ${task.pickedUpByName ?? 'Unknown user'}`;
  }
  return `${task.departmentName} · Ready to pick up`;
}

function taskTime(task: DepartmentTask): number {
  const value = task.finishedAt ?? task.startedAt ?? task.updatedAt;
  return Date.parse(value) || 0;
}

export function getWorkOrderProgress(order: WorkOrder): string[] {
  return order.items.flatMap((item, index) => {
    const prefix = `Item ${index + 1}`;
    if (!order.accountsApproved) return [`${prefix} · Accounts · Waiting for invoice`];

    const current = item.tasks
      .filter((task) => task.availableAt && task.status !== 'completed')
      .sort((left, right) => taskTime(left) - taskTime(right));
    if (current.length) {
      return current.map((task) => `${prefix} · ${taskProgressLabel(task)}`);
    }

    const latest = item.tasks
      .filter((task) => task.status === 'completed')
      .sort((left, right) => taskTime(right) - taskTime(left))[0];
    return latest ? [`${prefix} · ${taskProgressLabel(latest)}`] : [`${prefix} · Waiting for next department`];
  });
}
